/**
 * SOLOMON — DIAGRAM BLOCK
 * Renders Mermaid.js diagrams inline within chat messages.
 * Styled to match Hermès design system.
 *
 * Diagram syntax is embedded in messages as ```mermaid blocks.
 * Supports: flowchart, sequence, mind map, pie, timeline, etc.
 *
 * SECURITY NOTE (2026-04-15):
 * We do NOT run a second DOMPurify pass on the SVG output.
 * Mermaid's securityLevel: 'strict' already runs DOMPurify internally
 * (mermaid.core.mjs render function) with config:
 *   { ADD_TAGS: ["foreignobject"], ADD_ATTR: ["dominant-baseline"],
 *     HTML_INTEGRATION_POINTS: { foreignobject: true } }
 * This strips scripts, event handlers, and dangerous elements.
 * A second pass in SVG profile mode was STRIPPING legitimate text
 * elements (foreignObject contents, dominant-baseline attrs) causing
 * empty diagram bubbles. Removing it is both safe and necessary.
 */

import { Component, useRef, useEffect, useState, useId } from 'react';
import mermaid from 'mermaid';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { setLastDiagramError } from '../../context/SolomonContext';
import { normalizeSyntax } from './mermaidNormalize.js';

// ── Error boundary — prevents diagram crashes from breaking chat ──
class DiagramErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="diagram-block diagram-block--error">
          <pre className="diagram-source">{this.props.syntax}</pre>
          <span className="diagram-error-label">Diagram render failed: {this.state.error?.message || 'Unknown error'}</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Mermaid initialization ──────────────────────────────────
// mermaid.initialize is GLOBAL config. We re-run it whenever the active theme
// changes (light/dark) so a subsequent re-render emits the right palette.
// `lastInitTheme` guards against redundant re-initialization on the hot path.
let lastInitTheme = null;

function ensureInit(theme) {
  const isDark = theme !== 'light';
  if (lastInitTheme === (isDark ? 'dark' : 'light')) return;
  lastInitTheme = isDark ? 'dark' : 'light';

  mermaid.initialize({
    startOnLoad: false,

    // GLOBAL htmlLabels: false — forces ALL diagram types to use native
    // SVG <text> elements instead of <foreignObject> with HTML content.
    // This is the primary fix for empty bubbles.
    // Note: flowchart.htmlLabels is DEPRECATED in Mermaid 11 — the
    // top-level htmlLabels is the canonical setting.
    htmlLabels: false,

    theme: 'base',
    themeVariables: {
      // Palette is theme-reactive: ensureInit re-runs mermaid.initialize on
      // every light/dark toggle (see MutationObserver + key remount below), so
      // the next render bakes the correct colors into the SVG. There is NO CSS
      // filter — color comes entirely from these themeVariables per mode.
      primaryColor:      isDark ? '#2a1a0a' : '#FFF5EB',
      primaryTextColor:  isDark ? '#F0E0CC' : '#111111',
      primaryBorderColor: '#FF6B00',
      lineColor:         isDark ? '#C9A84C' : '#B87333',
      secondaryColor:    isDark ? '#1a1a1a' : '#F4F4F4',
      tertiaryColor:     isDark ? '#1E1E1E' : '#EBEBEB',
      noteBkgColor:      isDark ? '#1a1208' : '#FFF5EB',
      noteTextColor:     isDark ? '#F0E0CC' : '#111111',
      noteBorderColor:   '#C9A84C',
      // Text
      fontFamily:        'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize:          '13px',
      // Flowchart
      nodeBorder:        '#FF6B00',
      mainBkg:           isDark ? '#1a1208' : '#FFF5EB',
      clusterBkg:        isDark ? '#0E0E0E' : '#FAFAFA',
      clusterBorder:     isDark ? 'rgba(255,107,0,0.2)' : 'rgba(0,0,0,0.1)',
      edgeLabelBackground: isDark ? '#0E0E0E' : '#FAFAFA',
      // Sequence
      actorBkg:          isDark ? '#1a1208' : '#FFF5EB',
      actorBorder:       '#FF6B00',
      actorTextColor:    isDark ? '#F0E0CC' : '#111111',
      signalColor:       isDark ? '#F0E0CC' : '#111111',
      signalTextColor:   isDark ? '#F0E0CC' : '#111111',

      // ── Mindmap / pie / timeline SECTION palette (the "color scale") ──
      // Verified against the installed mermaid 11.14.0 source:
      //   • mindmap-definition reads section fills from options["cScale" + i]
      //     and label text from options["cScaleLabel" + i].
      //   • i = sectionNumber % THEME_COLOR_LIMIT, and THEME_COLOR_LIMIT = 12,
      //     so there are 12 slots (cScale0..cScale11 / cScaleLabel0..11).
      //   • The base theme assigns each as `this.cScaleN = this.cScaleN || …`,
      //     so values supplied here WIN (the `||` only fills unset slots).
      //
      // Why this fix exists: in dark mode the base theme derives the section
      // scale from our near-black primary/secondary/tertiary, collapsing every
      // mindmap node to a uniform black box (color hierarchy lost). We set an
      // EXPLICIT dark scale of distinct, dark-readable Hermès-warm hues. cScale0
      // (the root/center) stays Solomon orange. Each fill is paired with a
      // cScaleLabel chosen for WCAG-AA contrast (all pairs ≥4.7:1 — verified):
      //   0 center  #E8621A orange     label #1A1208 (5.46:1)
      //   1 gold    #C9A84C            label #1A1208 (8.10:1)
      //   2 sage    #6E9075            label #1A1208 (5.21:1)
      //   3 copper  #B87333            label #1A1208 (4.88:1)
      //   4 d.blue  #7E94B8            label #1A1208 (6.01:1)
      //   5 mauve   #A77BA0            label #1A1208 (5.28:1)
      //   6 terra   #C2674E            label #1A1208 (4.71:1)
      //   7 teal    #5E9B98            label #1A1208 (5.84:1)
      //   8 amber   #CC8F3C            label #1A1208 (6.67:1)
      //   9..11 cycle gold/sage/copper so >9-section maps never fall to black.
      //
      // LIGHT mode is intentionally left to mermaid's base-theme defaults
      // (operator-confirmed good, and never collapsed because light primary/
      // secondary/tertiary are already light). Overriding it risked regressing
      // that look, so we pass undefined and let the base scale stand.
      ...(isDark ? {
        cScale0:  '#E8621A', cScaleLabel0:  '#1A1208',
        cScale1:  '#C9A84C', cScaleLabel1:  '#1A1208',
        cScale2:  '#6E9075', cScaleLabel2:  '#1A1208',
        cScale3:  '#B87333', cScaleLabel3:  '#1A1208',
        cScale4:  '#7E94B8', cScaleLabel4:  '#1A1208',
        cScale5:  '#A77BA0', cScaleLabel5:  '#1A1208',
        cScale6:  '#C2674E', cScaleLabel6:  '#1A1208',
        cScale7:  '#5E9B98', cScaleLabel7:  '#1A1208',
        cScale8:  '#CC8F3C', cScaleLabel8:  '#1A1208',
        cScale9:  '#C9A84C', cScaleLabel9:  '#1A1208',
        cScale10: '#6E9075', cScaleLabel10: '#1A1208',
        cScale11: '#B87333', cScaleLabel11: '#1A1208',
      } : {}),
    },
    flowchart: {
      htmlLabels: false,   // Redundant with global setting but explicit for clarity
      curve: 'basis',
      padding: 12,
      // useMaxWidth:false → Mermaid emits the SVG at its natural intrinsic
      // width instead of stretching to 100%. Combined with the CSS
      // (.diagram-block overflow:auto + svg max-width:none), wide diagrams
      // render at a READABLE size and scroll, rather than shrink-to-illegible.
      useMaxWidth: false,
    },
    sequence: {
      diagramMarginX: 15,
      diagramMarginY: 15,
      actorMargin: 60,
      messageMargin: 30,
      useMaxWidth: false,
    },
    mindmap: {
      useMaxWidth: false,
    },
    gantt: {
      useMaxWidth: false,
    },
    securityLevel: 'strict',
  });
}

// ── Render timeout ──────────────────────────────────────────

function renderWithTimeout(diagramId, syntax, timeoutMs = 5000) {
  return Promise.race([
    mermaid.render(diagramId, syntax),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Diagram render timed out after 5s')), timeoutMs)
    ),
  ]);
}

// ── Pan/zoom helpers ────────────────────────────────────────

// Minimal inline-SVG icon controls (design-system: thin stroke, subtle).
// Rendered as a render-prop child of TransformWrapper so it gets live handles.
function DiagramControls({ zoomIn, zoomOut, resetTransform }) {
  return (
    <div className="diagram-controls" role="group" aria-label="Diagram zoom controls">
      <button
        type="button"
        className="diagram-control-btn"
        title="Zoom in"
        aria-label="Zoom in"
        onClick={() => zoomIn()}
      >+</button>
      <button
        type="button"
        className="diagram-control-btn"
        title="Zoom out"
        aria-label="Zoom out"
        onClick={() => zoomOut()}
      >&minus;</button>
      <button
        type="button"
        className="diagram-control-btn"
        title="Reset / fit to view"
        aria-label="Reset and fit to view"
        onClick={() => resetTransform()}
      >&#10239;</button>
    </div>
  );
}

// NOTE on "fit + centered": this used to be a JS routine (measure the SVG, scale
// it down, setTransform to dead-center). It lost a layout-measurement race on
// heavier diagrams (mindmaps) and left them rendered at natural size, panned
// off-center with branches clipped off the page. The fit is now done in pure CSS
// — `.diagram-block svg { width:100%; height:auto; max-width:100% }` scales the
// SVG to its field and the SVG's own viewBox + preserveAspectRatio center it, with
// NO measurement and NO race. So the initial transform stays at the library
// default (scale 1, origin) and that IS the fit. Pan/zoom only kicks in when the
// user zooms past fit; "Reset" returns to the scale-1 fit via resetTransform().

// ── Component ───────────────────────────────────────────────

// Module-load failures (stale lazy chunk after a deploy, network drop) are NOT
// syntax errors — mermaid lazy-imports its per-diagram-type chunks at render
// time, and a failed import rejects through the same catch as a parse error.
// Classify them so the label is honest and the message never reaches the AI
// regeneration feedback channel (a "'text/html' is not a valid JavaScript MIME
// type" message would prompt the model to "fix" valid syntax).
const MODULE_LOAD_ERROR_RE = /MIME type|dynamically imported module|Failed to fetch/i;

function DiagramBlockInner({ syntax }) {
  const containerRef = useRef(null);
  const [svg, setSvg]     = useState('');
  const [error, setError] = useState(null);
  const [isInfraError, setIsInfraError] = useState(false);
  const uniqueId = useId().replace(/:/g, '-');

  // Mermaid's palette is baked into the rendered SVG at render time and is NOT
  // reactive — track the app theme so the diagram re-initializes + re-renders
  // with the right colors when the user toggles light/dark (mirrors ChartBlock).
  const [theme, setTheme] = useState(() =>
    document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() =>
      setTheme(el.getAttribute('data-theme') === 'light' ? 'light' : 'dark')
    );
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!syntax?.trim()) return;

    // Issue 55: re-read the applied theme at render time. The useState
    // initializer can race app boot applying data-theme (a diagram mounting
    // in that window baked the dark palette into a light-mode SVG). If the
    // DOM disagrees with state, sync state and bail — the effect re-runs
    // with the correct theme instead of rendering with the stale one.
    const domTheme = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    if (domTheme !== theme) {
      setTheme(domTheme);
      return;
    }

    // Re-applies global mermaid config for the current theme before rendering,
    // so a theme toggle re-renders the SVG with the correct palette.
    ensureInit(theme);

    let cancelled = false;
    const diagramId = `mermaid-${uniqueId}-${Date.now()}`;
    const normalized = normalizeSyntax(syntax.trim());

    (async () => {
      try {
        const { svg: renderedSvg } = await renderWithTimeout(diagramId, normalized);
        if (!cancelled) {
          setSvg(renderedSvg);
          setError(null);
          setIsInfraError(false);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err.message || 'Unknown Mermaid syntax error';
          const infra = MODULE_LOAD_ERROR_RE.test(errorMsg);
          console.warn('[Solomon/Mermaid] Render error:', errorMsg);
          setError(errorMsg);
          setIsInfraError(infra);
          setSvg('');
          if (!infra) {
            // Store error so regeneration can include it as feedback to the AI.
            // Infra errors stay out of this channel — they aren't the model's to fix.
            setLastDiagramError(errorMsg);
          }
        }
        // Clean up failed render container mermaid may have created
        const orphan = document.getElementById('d' + diagramId);
        orphan?.remove();
      }
    })();

    return () => { cancelled = true; };
  }, [syntax, uniqueId, theme]);

  if (error) {
    return (
      <div className="diagram-block diagram-block--error">
        <pre className="diagram-source">{syntax}</pre>
        <span className="diagram-error-label">
          {isInfraError
            ? 'Diagram engine failed to load (new version deployed — refresh to update)'
            : `Diagram syntax error: ${error}`}
        </span>
      </div>
    );
  }

  if (!svg) return null;

  // SVG is already sanitized by Mermaid's internal DOMPurify (securityLevel: 'strict').
  // No second sanitization pass — see SECURITY NOTE at top of file.
  //
  // The sanitized SVG is wrapped in a frameless react-zoom-pan-pinch canvas:
  //  • Initial fit + centering is done in CSS (see the SVG sizing rules in
  //    chat.css and the NOTE above the component) — the SVG scales to its field
  //    and self-centers via its viewBox, so the library default transform
  //    (scale 1, origin) already shows the whole diagram centered. No onInit JS,
  //    no measurement race.
  //  • minScale 0.2 / maxScale 5, limitToBounds:false → free pan + deep zoom.
  //  • wheel.activationKeys ['Control','Meta'] → wheel zooms ONLY with Ctrl/⌘
  //    held; a bare wheel over the diagram lets the PAGE scroll normally
  //    (no scroll hijack).
  //  • doubleClick mode 'zoomIn' → double-click zooms toward the cursor.
  //  • key={theme + svg} → on a theme toggle the SVG re-renders (existing
  //    behavior) and the wrapper REMOUNTS, so the transform never ends up
  //    stale/mis-transformed against the new SVG.
  return (
    <div className="diagram-block">
      <TransformWrapper
        key={`${theme}-${svg.length}`}
        minScale={0.2}
        maxScale={5}
        initialScale={1}
        // Pin the initial transform to translate(0,0) and let pure CSS do the
        // centering: the wrapper is forced full-width, the content fills it, and the
        // SVG centers via margin:0 auto. centerOnInit / the library's own auto-center
        // kept spawning slightly off (transform fought the CSS) — so we disable it
        // and own the position deterministically. translate(0,0) over a CSS-centered
        // layout = centered on spawn; pan/zoom still works from there.
        centerOnInit={false}
        initialPositionX={0}
        initialPositionY={0}
        limitToBounds={false}
        wheel={{ step: 0.08, activationKeys: ['Control', 'Meta'] }}
        panning={{ disabled: false, velocityDisabled: false }}
        doubleClick={{ mode: 'zoomIn' }}
      >
        {(utils) => (
          <>
            <DiagramControls
              zoomIn={utils.zoomIn}
              zoomOut={utils.zoomOut}
              resetTransform={() => utils.resetTransform()}
            />
            <span className="diagram-zoom-hint" aria-hidden="true">⌘/Ctrl + scroll to zoom</span>
            <TransformComponent
              wrapperClass="diagram-canvas"
              contentStyle={{ display: 'block' }}
            >
              <div
                ref={containerRef}
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
}

// Wrap with error boundary to prevent diagram crashes from breaking the chat
export default function DiagramBlock({ syntax }) {
  return (
    <DiagramErrorBoundary syntax={syntax}>
      <DiagramBlockInner syntax={syntax} />
    </DiagramErrorBoundary>
  );
}
